// Talks to Shopify's Storefront API cart. Shopify is the source of truth for prices, stock,
// discounts and checkout; we only keep the cart id. Works without an access token
// (tokenless Storefront access: products, collections and cart).
import { createGraphQLClient } from '@shopify/graphql-client';

export const API_VERSION = '2026-07';

const CART_FIELDS = `
  id checkoutUrl totalQuantity
  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
  discountCodes { code applicable }
  discountAllocations { discountedAmount { amount } }
  lines(first: 100) { nodes {
    id quantity
    cost { totalAmount { amount } subtotalAmount { amount } }
    discountAllocations { discountedAmount { amount } }
    attributes { key value }
    merchandise { ... on ProductVariant {
      id title availableForSale
      price { amount }
      image { url altText }
      product { handle title }
    } }
  } }`;
const RESULT = `cart { ${CART_FIELDS} } userErrors { field message code } warnings { code message }`;

export const DOCS = {
  get: `query CartQuery($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }`,
  create: `mutation CartCreate($input: CartInput!) { cartCreate(input: $input) { ${RESULT} } }`,
  add: `mutation CartLinesAdd($id: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $id, lines: $lines) { ${RESULT} } }`,
  update: `mutation CartLinesUpdate($id: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $id, lines: $lines) { ${RESULT} } }`,
  remove: `mutation CartLinesRemove($id: ID!, $lineIds: [ID!]!) { cartLinesRemove(cartId: $id, lineIds: $lineIds) { ${RESULT} } }`,
  discounts: `mutation CartDiscountCodesUpdate($id: ID!, $codes: [String!]!) { cartDiscountCodesUpdate(cartId: $id, discountCodes: $codes) { ${RESULT} } }`,
};

export class NetworkError extends Error { constructor(msg, cause) { super(msg); this.name = 'NetworkError'; this.cause = cause; } }

// transport.run(name, variables) -> data (throws NetworkError on transport/GraphQL failure)
export function createShopifyTransport({ storeDomain, apiVersion = API_VERSION, fetchApi, retries = 1 }) {
  const client = createGraphQLClient({
    url: `${storeDomain.replace(/\/$/, '')}/api/${apiVersion}/graphql.json`,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    retries, customFetchApi: fetchApi || ((...a) => fetch(...a)),
  });
  return {
    async run(name, variables) {
      let res;
      try { res = await client.request(DOCS[name], { variables }); }
      catch (e) { throw new NetworkError('Could not reach the shop', e); }
      if (res.errors) throw new NetworkError(res.errors.message || 'The shop returned an error', res.errors);
      return res.data;
    },
  };
}

export const toGid = (variantId) => String(variantId).startsWith('gid://') ? String(variantId) : `gid://shopify/ProductVariant/${variantId}`;
export const fromGid = (gid) => Number(String(gid).split('/').pop());

// Shopify's cart -> the plain shape the UI uses. Amounts are numbers in rand.
export function normalizeCart(c) {
  if (!c) return null;
  const n = (m) => (m ? Number(m.amount) : 0);
  const lines = c.lines.nodes.filter(l => l.quantity > 0).map(l => {
    const m = l.merchandise || {};
    return {
      id: l.id, quantity: l.quantity,
      variantId: fromGid(m.id), variantTitle: m.title === 'Default Title' ? '' : m.title,
      handle: m.product?.handle, productTitle: m.product?.title,
      image: m.image?.url || null, price: n(m.price),
      subtotal: n(l.cost?.subtotalAmount) || n(m.price) * l.quantity, total: n(l.cost?.totalAmount),
      discount: (l.discountAllocations || []).reduce((a, d) => a + n(d.discountedAmount), 0),
      available: m.availableForSale !== false,
      attributes: Object.fromEntries((l.attributes || []).map(a => [a.key, a.value])),
    };
  });
  const lineDiscounts = lines.reduce((a, l) => a + l.discount, 0);
  const orderDiscounts = (c.discountAllocations || []).reduce((a, d) => a + n(d.discountedAmount), 0);
  return {
    id: c.id, checkoutUrl: c.checkoutUrl, count: lines.reduce((a, l) => a + l.quantity, 0),
    subtotal: n(c.cost.subtotalAmount), total: n(c.cost.totalAmount), currency: c.cost.totalAmount?.currencyCode || 'ZAR',
    discount: lineDiscounts + orderDiscounts, discountCodes: c.discountCodes || [], lines,
  };
}
